import { useEffect, useState } from 'react';
import { Calendar, CheckCircle2, ExternalLink } from 'lucide-react';
import ErrorBanner from './ErrorBanner.jsx';
import { googleCalendarAPI } from '../services/api.js';
import { useAuth } from '../contexts/AuthContext.jsx';

// "Connect Google Calendar" — an OAuth redirect flow (not a creds form), mirroring
// the Zoom card. Three states: NOT-CONFIGURED (server has no Google OAuth client →
// unavailable), DISCONNECTED (show Connect), CONNECTED (show account email +
// Disconnect). Connect/Disconnect are admin-only, matching Zoom. No secrets/tokens
// ever reach the browser — we only handle a consent URL + status flags.
// Backend: GET/DELETE /integrations/google-calendar, GET .../connect (Charlie).
//
// `refreshKey` lets a parent (the /settings/integrations callback page) force a
// re-fetch after Google redirects back with ?calendar=connected.
export default function GoogleCalendarCard({ refreshKey }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [state, setState] = useState({ connected: false, configured: true, accountEmail: null });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const [notReady, setNotReady] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const s = await googleCalendarAPI.status();
      setState({
        connected: !!s?.connected,
        // Default configured=true only when the field is absent, so a real
        // configured:false (no OAuth client) correctly shows "unavailable".
        configured: s?.configured !== false,
        accountEmail: s?.accountEmail || null
      });
    } catch (err) {
      if (err.status === 404) setNotReady(true); // backend not deployed yet
      else setError(err.body?.message || err.message || 'Failed to load Google Calendar status.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    setConnecting(true);
    setError('');
    try {
      const { url } = await googleCalendarAPI.connectUrl();
      if (url) window.location.href = url; // full-page redirect to Google consent
      else setError('Could not start the Google connection.');
    } catch (err) {
      if (err.status === 404) setNotReady(true);
      else setError(err.body?.message || err.message || 'Could not start the Google connection.');
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!confirm('Disconnect Google Calendar? The assistant will no longer read or add events to your calendar.')) return;
    setError('');
    try {
      await googleCalendarAPI.remove();
      await load();
    } catch (err) {
      setError(err.body?.message || err.message || 'Disconnect failed.');
    }
  };

  return (
    <section className="card">
      <div className="flex items-center gap-2 mb-1">
        <Calendar size={18} className="text-alfred-gold" />
        <h2 className="text-xl text-stone-100">Google Calendar</h2>
        {state.connected && (
          <span className="ml-2 badge bg-emerald-900/40 text-emerald-300 border border-emerald-800 text-[10px] inline-flex items-center gap-1">
            <CheckCircle2 size={10} /> Connected
          </span>
        )}
      </div>
      <p className="text-sm text-stone-400 mb-4">
        Connect your Google Calendar so the assistant can check your availability and add events
        when it schedules on your behalf.
      </p>

      {notReady && (
        <p className="mb-4 rounded-lg border border-amber-800/50 bg-amber-950/30 px-4 py-2 text-sm text-amber-200">
          Google Calendar connection is being set up on the backend — this will go live shortly.
        </p>
      )}
      {error && <div className="mb-4"><ErrorBanner message={error} /></div>}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : notReady ? null : !state.configured ? (
        <p className="text-sm text-stone-500">
          Google Calendar isn't available on this account yet — please contact your administrator to enable it.
        </p>
      ) : state.connected ? (
        <div className="space-y-3 max-w-xl">
          <p className="text-sm text-stone-300">
            Connected{state.accountEmail ? <> as <span className="text-stone-100">{state.accountEmail}</span></> : ''}.
          </p>
          {isAdmin ? (
            <button type="button" className="btn-ghost text-red-300 hover:text-red-200" onClick={disconnect}>
              Disconnect
            </button>
          ) : (
            <p className="text-xs text-stone-500">Only an account admin can change this connection.</p>
          )}
        </div>
      ) : (
        <div className="space-y-3 max-w-xl">
          {isAdmin ? (
            <button type="button" className="btn-primary" onClick={connect} disabled={connecting}>
              <ExternalLink size={14} /> {connecting ? 'Opening Google…' : 'Connect Google Calendar'}
            </button>
          ) : (
            <p className="text-sm text-stone-500">
              Not connected. Ask an account admin to connect Google Calendar.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
